import * as FramerMotion from 'framer-motion'
import * as React from 'react'
import * as Urql from 'urql'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'

import * as Common from '@rushplay/common'
import * as I18n from '@rushplay/i18n'
import css from '@styled-system/css'
import styled from '@emotion/styled'

import * as Constants from './constants'
import * as Icons from './icons'
import { Spinner } from './spinner'

const query = gql`
  query providers {
    providers {
      name
      image
      type
    }
  }
`

const ProviderButton = styled('div', {
  shouldForwardProp: Common.noneOf(['selected']),
})`
  ${props =>
    !props.selected &&
    css({
      '&:hover': {
        borderColor: 'provider-border-hover',
      },
    })}
  ${props =>
    css({
      position: 'relative',
      zIndex: 99,
      width: '109px',
      height: '48px',
      marginTop: 0,
      borderRadius: '4px',
      border: '2px solid',
      padding: 0,
      borderColor: props.selected
        ? 'provider-border-selected'
        : 'provider-border',
      opacity: '100%',
      cursor: 'pointer',
      transition: '.3s',
    })}
`

function getVariants(isDesktop) {
  return {
    open: {
      height: 'auto',
      overflow: 'hidden',
      transition: {
        duration: 0.2,
      },
      transitionEnd: {
        height: 'auto',
      },
    },
    closed: {
      overflow: 'hidden',
      height: isDesktop ? '56px' : '112px',
      transitionEnd: { height: isDesktop ? '56px' : '112px' },
    },
  }
}

const chevronVariants = {
  up: {
    transform: 'rotate(-90deg)',
    transition: {
      duration: 0.2,
    },
    transitionEnd: {
      transform: 'rotate(-90deg)',
    },
  },
  down: {
    transform: 'rotate(90deg)',
    transitionEnd: { transform: 'rotate(90deg)' },
  },
}

export function SearchProvider(props) {
  const i18n = I18n.useI18n()
  const [{ data = {}, fetching }] = Urql.useQuery({ query })
  const { providers = [] } = data
  const [collapsed, setCollapsed] = React.useState(true)

  function toggle() {
    setCollapsed(!collapsed)
  }

  if (fetching) {
    return <Spinner delay />
  }

  if (!providers.length) {
    return null
  }

  return (
    <>
      <Common.Box
        display="flex"
        flex="1"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={0}
        minHeight={['40px', 'auto']}
        zIndex="99"
        position="relative"
      >
        <Common.Box
          display="flex"
          alignItems={['flex-start', 'center']}
          flexDirection={['column', 'row']}
        >
          <Common.Text fontWeight="bold" fontSize={3} color="g-text">
            {i18n.translate('search.provider')}
          </Common.Text>
          {Boolean(props.selectedProviders.length) && (
            <Common.Box
              ml={[null, 0]}
              mt={[0, '0px']}
              display="flex"
              fontWeight="bold"
              fontSize={2}
            >
              <Common.Text>
                {i18n.translate('search.provider.selected-providers', {
                  selectedProviders: props.selectedProviders.length,
                })}
              </Common.Text>
              <Common.Box
                ml={0}
                cursor="pointer"
                onClick={props.onProviderSearchReset}
              >
                <Common.Text
                  ml="10px"
                  color="provider-button-selected"
                  textDecoration="underline"
                  fontSize={2}
                >
                  {i18n.translate('search.provider.reset-providers')}
                </Common.Text>
              </Common.Box>
            </Common.Box>
          )}
        </Common.Box>
        <Common.Box
          onClick={toggle}
          display="flex"
          alignItems="center"
          cursor="pointer"
          color="g-text"
        >
          <Common.Text fontSize={2}>
            {i18n.translate('search.show-all')}
          </Common.Text>
          <FramerMotion.motion.div
            variants={chevronVariants}
            animate={collapsed ? 'up' : 'down'}
          >
            <Icons.KeyboardArrowLeft />
          </FramerMotion.motion.div>
        </Common.Box>
      </Common.Box>
      <FramerMotion.motion.div
        variants={getVariants(Constants.isDesktop)}
        animate={collapsed ? 'closed' : 'open'}
        overflow="hidden"
      >
        <Common.Box
          display={['flex', 'grid']}
          gridTemplateColumns={[null, 'repeat(auto-fit, 110px)']}
          gridColumnGap={['8px', '16px']}
          justifyContent="space-between"
          flexWrap="wrap"
        >
          {providers.map(provider => {
            return (
              <ProviderButton
                selected={props.selectedProviders.includes(provider.name)}
                key={provider.name}
                onClick={() =>
                  props.onChange({
                    name: provider.name,
                    type: `${provider.type}s`,
                  })
                }
              >
                <Common.Box
                  width="100%"
                  height="100%"
                  flex="1"
                  backgroundImage={`url(${provider.image})`}
                  backgroundRepeat="no-repeat"
                  backgroundSize="95% auto"
                  backgroundPosition="center"
                  color="static-white"
                  transition=".3s"
                />
              </ProviderButton>
            )
          })}
        </Common.Box>
      </FramerMotion.motion.div>
    </>
  )
}

SearchProvider.propTypes = {
  selectedProviders: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onProviderSearchReset: PropTypes.func.isRequired,
}

export default SearchProvider
